import { z } from "zod";

export const themeColorStopSchema = z
  .object({
    hue: z.number().min(0).max(360),
    chroma: z.number().min(0).max(100),
    x: z.number().min(0).max(1),
    y: z.number().min(0).max(1),
  })
  .describe("ThemeColorStop");

// A color-theory relationship that turns one seed hue into a set of
// harmonious-but-separated chart series. The palette is always derived from
// (seed, scheme) — never hand-picked — so it stays generative and on-brand.
export const chartHarmonySchema = z
  .object({
    scheme: z.enum([
      "complementary",
      "splitComplementary",
      "triadic",
      "tetradic",
      "analogous",
      "monochromatic",
    ]),
    // Anchor hue for the series. Defaults to the brand seed (colors[0]) when
    // omitted, letting charts diverge from the brand color when desired.
    seedHue: z.number().min(0).max(360).optional(),
  })
  .describe("ChartHarmony");

export const themeConfigSchema = z
  .object({
    colors: z.array(themeColorStopSchema).length(1),
    mode: z.enum(["light", "dark", "system"]),
    intensity: z.number().min(0).max(100),
    chart: chartHarmonySchema.optional(),
  })
  .describe("ThemeConfig");

// Coerces legacy DB rows into the current shape without a migration:
//   - Old "vibrant" mode → "light" (both rendered on a light background)
//   - Multi-stop gradients → single first stop (gradients are gone)
// Extra fields (grain, texture) are dropped by zod's default strip behavior.
const legacyThemeConfigShape = z
  .object({
    mode: z.unknown(),
    colors: z.unknown(),
  })
  .passthrough()
  .nullable();

function coerceLegacyThemeConfig(value: unknown): unknown {
  const parsed = legacyThemeConfigShape.safeParse(value ?? null);
  if (!parsed.success || !parsed.data) {
    return value;
  }
  const raw = parsed.data;
  const next: Record<string, unknown> = { ...raw };
  if (raw.mode === "vibrant") {
    next.mode = "light";
  }
  if (Array.isArray(raw.colors) && raw.colors.length > 1) {
    next.colors = [raw.colors[0]];
  }
  return next;
}

export function parseThemeConfigFromDatabase(value: unknown) {
  const result = themeConfigSchema
    .nullable()
    .safeParse(coerceLegacyThemeConfig(value) ?? null);
  return result.success ? result.data : null;
}
